import React, { useState, useEffect, useRef } from 'react';
import './MiningRigMenu.scss';
import { updateMiningRigs, getConfigValue, initializeTotalMiningPower } from '../../backend/services/gameData';

// Optimized Mining Rig Menu Component with Tiered Mining System
const MiningRigMenu = React.memo(({ onClose, gameData, updateGameData, refreshMoneyDisplay }) => {
  // Game state from props
  const [localMoney, setLocalMoney] = useState(() => gameData?.money || 0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [energy, setEnergy] = useState(() => gameData?.data?.energy || 100);
  const [showPausedInfo, setShowPausedInfo] = useState(false); // State for tooltip/modal
  const [totalMiningPowerFromDB, setTotalMiningPowerFromDB] = useState(null); // Total mining power from database
  
  // Get mining rigs array from game data or initialize
  const [miningRigs, setMiningRigs] = useState(() => {
    return Array.isArray(gameData?.data?.miningRigs) ? gameData.data.miningRigs : [];
  });
  
  // Use constants from gameData or fallback
  const gameConstants = gameData?.constants || {};
  const maxRigs = gameConstants.MAX_RIGS || 4;
  
  // Get tier values from constants or use fallbacks
  const rigTierPowers = gameConstants.RIG_TIER_POWERS || [10, 15, 25, 40];
  const rigTierCosts = gameConstants.RIG_TIER_COSTS || [500, 2000, 10000, 50000];
  const rigTierEfficiency = gameConstants.RIG_TIER_EFFICIENCY || [1, 1.2, 1.5, 2];
  const dailyTokenPool = gameConstants.DAILY_TOKEN_POOL || 2000;
  const minWalletBalance = gameConstants.MIN_WALLET_BALANCE_FOR_MINING || 15000; // Get the constant
  
  // Define tier names (these are UI-only and not needed in backend constants)
  const tierNames = ["Starter Rig", "Standard Rig", "Advanced Rig", "Enterprise Rig"];
  
  // Get user's mining power from save data
  const userMiningPower = gameData?.mining_power || 0;
  
  // Calculate total mining power from rigs (for display purposes)
  const calculatedMiningPower = miningRigs.reduce((total, rig) => {
    if (rig.isActive) {
      return total + rigTierPowers[rig.tier - 1];
    }
    return total;
  }, 0);
  
  // Calculate tokens per hour using database-based formula
  // Formula: (2000 / 288) * (userMiningPower / totalMiningPowerFromDB) * 12
  // 2000 = daily total, 288 = 5-minute intervals per day, 12 = intervals per hour
  let tokensPerHour = 0;
  if (totalMiningPowerFromDB && totalMiningPowerFromDB > 0 && userMiningPower > 0) {
    const tokensPerInterval = (2000 / 288) * (userMiningPower / totalMiningPowerFromDB);
    tokensPerHour = tokensPerInterval * 12; // Convert to hourly (12 intervals per hour)
  }
  
  // Flag to avoid re-renders when updating gameData
  const isUpdatingRef = useRef(false);
  
  // Fetch total mining power from database
  useEffect(() => {
    const fetchTotalMiningPower = async () => {
      try {
        const { value, error, notFound } = await getConfigValue('total_mining_power');
        if (error) {
          console.error('Error fetching total mining power:', error);
          setTotalMiningPowerFromDB(1); // Fallback to prevent division by zero
        } else if (notFound) {
          console.warn('total_mining_power config not found in database, using default value of 10');
          setTotalMiningPowerFromDB(10); // Default to 10 as mentioned by user
        } else {
          const totalPower = Number(value) || 10;
          setTotalMiningPowerFromDB(totalPower);
        }
      } catch (error) {
        console.error('Unexpected error fetching total mining power:', error);
        setTotalMiningPowerFromDB(10); // Fallback to 10
      }
    };
    
    fetchTotalMiningPower();
  }, []);
  
  // Debug logging for gameData structure on mount
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // Only log structure in development
      console.log("MiningRigMenu mounted", { 
        hasId: !!gameData?.id,
        hasUpdateCallbacks: !!updateGameData && !!refreshMoneyDisplay,
        constants: gameConstants
      });
    }
  }, [gameData, refreshMoneyDisplay, gameConstants]);
  
  // Update local state when gameData changes
  useEffect(() => {
    if (!isUpdatingRef.current && gameData?.data) {
      if (Array.isArray(gameData.data.miningRigs)) {
        setMiningRigs(gameData.data.miningRigs);
      }
      
      setLocalMoney(gameData.money || 0);
      setEnergy(gameData.data.energy || 100);
    }
  }, [gameData]);
  
  // Extract has_redicks status from gameData
  const hasRedicks = gameData?.has_redicks === true; // Default to false if undefined
  console.log("[MiningRigMenu] Calculated hasRedicks:", hasRedicks); // Log the result of the check
  
  // Prevent events from propagating to canvas
  const handleMenuClick = (e) => {
    e.stopPropagation();
  };
  
  const handleMiningStatusClick = (e) => {
    e.stopPropagation();
    if (!hasRedicks) {
      setShowPausedInfo(true);
    }
  };
  
  const closePausedInfo = (e) => {
    e.stopPropagation();
    setShowPausedInfo(false);
  };
  
  const handleBuyRig = async (tier) => {
    // Check if already processing
    if (isProcessing) {
      return;
    }

    // Validate purchase conditions
    if (localMoney < rigTierCosts[tier-1]) {
      console.log(`Cannot purchase tier ${tier} rig. Money: ${localMoney}, Cost: ${rigTierCosts[tier-1]}`);
      return;
    }

    // Exit pointer lock if active
    if (document.pointerLockElement) {
      document.exitPointerLock();
    }
    
    // Set processing state
    setIsProcessing(true);
    
    // Calculate new values
    const newRig = {
      tier,
      efficiencyMultiplier: rigTierEfficiency[tier-1],
      isActive: true,
      totalMined: 0
    };
    
    const updatedRigs = [...miningRigs, newRig];
    const updatedMoney = localMoney - rigTierCosts[tier-1];
    
    // Set the updating flag
    isUpdatingRef.current = true;
    
    try {
      // Get the save ID from gameData
      const saveId = gameData?.id;
      
      if (!saveId) {
        throw new Error("Invalid save ID");
      }

      // Calculate new total mining power
      const newTotalPower = updatedRigs.reduce((total, rig) => {
        if (rig.isActive) {
          return total + rigTierPowers[rig.tier - 1];
        }
        return total;
      }, 0);

      // Call backend to update mining rigs
      const { success, save, error, constants } = await updateMiningRigs(
        saveId,
        updatedRigs,
        updatedMoney,
        newTotalPower
      );
      
      if (error) {
        console.error("Error updating mining rigs:", error);
        if (error.rateLimit) {
          const errorMessage = document.createElement('div');
          errorMessage.className = 'rate-limit-error';
          errorMessage.textContent = `Please wait before making another purchase (${error.retryAfter}s)`;
          document.body.appendChild(errorMessage);
          
          setTimeout(() => {
            if (errorMessage.parentNode) {
              errorMessage.parentNode.removeChild(errorMessage);
            }
          }, error.retryAfter * 1000);
        }
      } else if (success && save) {
        // Server confirmed transaction was valid
        setMiningRigs(save.data.miningRigs || []);
        setLocalMoney(save.money || updatedMoney);
        setEnergy(save.data.energy || 100);
        
        // Update parent's gameData
        if (updateGameData) {
          const updatedGameDataObj = {
            ...gameData,
            data: {
              ...gameData.data,
              miningRigs: save.data.miningRigs,
              energy: save.data.energy || 100
            },
            money: save.money || updatedMoney,
            mining_power: save.mining_power || newTotalPower,
            constants: constants || gameData.constants
          };
          
          updateGameData(updatedGameDataObj);
          
          if (refreshMoneyDisplay) {
            refreshMoneyDisplay();
            setTimeout(refreshMoneyDisplay, 300);
          }
        }
      }
    } catch (error) {
      console.error("Error purchasing mining rig:", error);
    } finally {
      isUpdatingRef.current = false;
      setIsProcessing(false);
    }
  };
  
  return (
    <div className="mining-rig-menu" onClick={handleMenuClick}>
      <div className="mining-rig-content">
        <button className="close-button" onClick={onClose} aria-label="Close">×</button>
        <h2>Mining Rigs</h2>
        
        <div className="mining-stats">
          <div className="stat">
            <span className="stat-label">REDDICKS Balance:</span>
            <span className="stat-value">${localMoney.toLocaleString()}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Mining Power:</span>
            <span className="stat-value">{userMiningPower} Power</span>
          </div>
          <div className="stat">
            <span className="stat-label">Production Rate:</span>
            <span className="stat-value">{tokensPerHour.toFixed(2)} REDDICKS/hr</span>
          </div>
          <div className="stat">
            <span className="stat-label">Energy:</span>
            <span className="stat-value">
              <div className="energy-bar">
                <div className="energy-fill" style={{ width: `100%` }}></div>
              </div>
              <span className="energy-text">
                Unlimited/100
                <span className="energy-info-icon" title="Energy system is currently in development">i</span>
              </span>
            </span>
          </div>
          {/* Mining Status Indicator */}
          <div className="stat mining-status-stat">
            <span className="stat-label">Mining Status:</span>
            {hasRedicks ? (
              <span className="stat-value mining-active">ACTIVE MINING</span>
            ) : (
              <button className="stat-value mining-paused" onClick={handleMiningStatusClick}>
                MINING PAUSED
                <span className="info-icon">?</span>
              </button>
            )}
          </div>
        </div>
        
        <div className="mining-rigs-grid">
          {[...Array(maxRigs)].map((_, index) => {
            const tier = index + 1;
            const isOwned = miningRigs.some(rig => rig.tier === tier);
            const canAfford = localMoney >= rigTierCosts[index];
            const rigPower = rigTierPowers[index];
            
            return (
              <div key={index} className={`mining-rig-item ${isOwned ? 'owned' : 'available'}`}>
                <div className="rig-container">
                  <div className={`rig-icon tier-${tier}`}>
                    {isOwned ? (
                      <div className="owned-rig">
                        <div className="rig-lights"></div>
                        <div className="rig-body"></div>
                      </div>
                    ) : (
                      <div className="empty-rig">
                        <div className="rig-outline"></div>
                      </div>
                    )}
                  </div>
                  <div className="rig-info">
                    <span className="rig-number">{tierNames[index]}</span>
                    <span className="rig-status">
                      {isOwned ? 
                        (miningRigs.find(rig => rig.tier === tier)?.isActive ? 'Active' : 'Inactive') : 
                        'Not Purchased'}
                    </span>
                    {isOwned ? (
                      <>
                        <span className="rig-production">+{rigPower} Mining Power</span>
                        <span className="rig-efficiency">Efficiency: {rigTierEfficiency[index]}x</span>
                      </>
                    ) : (
                      <>
                        <span className="rig-production">+{rigPower} Mining Power</span>
                        <span className="rig-price">Price: ${rigTierCosts[index].toLocaleString()}</span>
                      </>
                    )}
                  </div>
                  {!isOwned && (
                    <button 
                      className={`buy-rig-button ${!canAfford || isProcessing ? 'cannot-afford' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBuyRig(tier);
                      }}
                      disabled={!canAfford || isProcessing}
                    >
                      {isProcessing ? 
                        'Processing...' : 
                        !canAfford ? 
                          `Need $${(rigTierCosts[index] - localMoney).toLocaleString()}` : 
                          `Buy for $${rigTierCosts[index].toLocaleString()}`
                      }
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="mining-info">
          <p>Mining Power determines your share of the daily token pool ({dailyTokenPool.toLocaleString()} REDDICKS)</p>
          <p>Your mining rigs produce {tokensPerHour.toFixed(2)} REDDICKS per hour</p>
        </div>
      </div>

      {/* Tooltip/Modal for paused mining */}
      {showPausedInfo && (
        <div className="mining-paused-tooltip" onClick={(e) => e.stopPropagation()}> {/* Prevent closing when clicking inside */}
          <div className="tooltip-content">
            <h3>Mining Paused</h3>
            <p>
              To be eligible for mining rewards, you must hold at least
              <strong> {minWalletBalance} $REDDICKS</strong> in your connected wallet.
            </p>
            <p>
              This requirement helps ensure rewards go to active community members
              and discourages automated farming.
            </p>
            <button className="tooltip-close-button" onClick={closePausedInfo}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
});

MiningRigMenu.displayName = 'MiningRigMenu';

export default MiningRigMenu; 